import { useState, useEffect } from 'react'
import { supabase } from './supabase'

// Types for the return value of get_brand_role function
export type BrandRoleResponse = {
  account_role: 'owner' | 'member'
  is_primary_owner: boolean
  is_personal_account: boolean
}

export function useBrandRole(brandId: string | null) {
  const [role, setRole] = useState<BrandRoleResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!brandId) {
      setLoading(false)
      return
    }

    async function fetchBrandRole() {
      try {
        const { data, error } = await supabase
          .rpc('get_brand_role', { brand_id: brandId as string })

        if (error) {
          console.error('Error fetching brand role:', error)
          setError(error)
          return
        }

        setRole(data as BrandRoleResponse)
      } catch (err) {
        console.error('Error in fetchBrandRole:', err)
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setLoading(false)
      }
    }

    fetchBrandRole()
  }, [brandId])

  // Helper functions for common role checks
  const isOwner = role?.account_role === 'owner'
  const isMember = !!role // If we have a role, the user is at least a member
  const isPrimaryOwner = role?.is_primary_owner === true

  return {
    role,
    loading,
    error,
    isOwner,
    isMember,
    isPrimaryOwner,
  }
}

// Function to check if a user has permission for specific brand actions
// Use this in components to conditionally render UI elements
export function useBrandPermissions(brandId: string | null) {
  const { isOwner, isMember, isPrimaryOwner, loading, error } = useBrandRole(brandId)

  return {
    // Members can view and edit brand information
    canViewBrand: isMember,
    canEditBrand: isMember,

    // Only owners can manage team members, billing, etc.
    canManageTeam: isOwner,
    canManageBilling: isOwner,

    // Only the primary owner can delete the brand or transfer ownership
    canDeleteBrand: isPrimaryOwner,
    canTransferOwnership: isPrimaryOwner,

    // Loading and error states
    loading,
    error,
  }
}